import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, MapPin, Mail, Phone } from "lucide-react";
import { navLinks, services, contactInfo } from "@/data/content";

const Footer = () => (
  <footer data-testid="site-footer" className="relative overflow-hidden bg-navy-deep text-white/65">
    <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-gold/5 blur-3xl" />
    <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-20 lg:px-10">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to="/" data-testid="footer-logo" className="flex items-center gap-3">
            <img src="/logo-white.png" alt="Vobis Credit" className="h-12 w-auto object-contain" />
          </Link>
          <p className="mt-6 max-w-xs text-sm leading-relaxed">
            Acquisto, gestione e valorizzazione di portafogli di crediti deteriorati NPL e UTP, al servizio di banche, investitori e imprese.
          </p>
          <div className="mt-6 flex gap-3">
            {[
              { icon: Linkedin, slug: "linkedin", href: "https://www.linkedin.com" },
              { icon: Instagram, slug: "instagram", href: "https://www.instagram.com" },
              { icon: Facebook, slug: "facebook", href: "https://www.facebook.com" },
            ].map(({ icon: Icon, slug, href }) => (
              <a
                key={slug}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-link-${slug}`}
                aria-label={slug}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/60 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:text-gold"
              >
                <Icon size={17} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Pagine</h3>
          <ul className="mt-6 space-y-3 text-sm">
            {navLinks.map((l) => (
              <li key={l.path}>
                <Link to={l.path} data-testid={`footer-link-${l.slug}`} className="transition-colors duration-300 hover:text-gold-light">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Servizi</h3>
          <ul className="mt-6 space-y-3 text-sm">
            {services.map((s) => (
              <li key={s.id}>
                <Link to="/servizi" data-testid={`footer-service-${s.id}`} className="transition-colors duration-300 hover:text-gold-light">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Contatti</h3>
          <ul className="mt-6 space-y-4 text-sm">
            {contactInfo.offices.map((o) => (
              <li key={o.label} className="flex gap-3">
                <MapPin size={17} className="mt-0.5 shrink-0 text-gold" />
                <span>
                  <span className="block font-medium text-white/85">{o.label}</span>
                  {o.address}
                </span>
              </li>
            ))}
            <li className="flex gap-3">
              <Mail size={17} className="mt-0.5 shrink-0 text-gold" />
              <a href={`mailto:${contactInfo.email}`} data-testid="footer-email" className="transition-colors hover:text-gold-light">
                {contactInfo.email}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone size={17} className="mt-0.5 shrink-0 text-gold" />
              <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="transition-colors hover:text-gold-light">
                {contactInfo.phone}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row">
        <p data-testid="footer-legal-info">
          Vobis Credit S.r.l. · P.IVA 18541881001 · REA RM1791277
        </p>
        <div className="flex items-center gap-6">
          <Link to="/privacy" data-testid="footer-link-privacy" className="transition-colors hover:text-gold-light">Privacy Policy</Link>
          <Link to="/cookie" data-testid="footer-link-cookie" className="transition-colors hover:text-gold-light">Cookie Policy</Link>
        </div>
        <p>© 2026 Vobis Credit S.r.l. — Tutti i diritti riservati</p>
      </div>
    </div>
  </footer>
);

export default Footer;
